const selectors = {
  message: '[data-role="message"]',
  fill: '[data-role="fill"]',
  progressbar: '[role="progressbar"]'
}

class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController()

    this.messageEl = this.querySelector(selectors.message)
    this.fillEl = this.querySelector(selectors.fill)
    this.progressbarEl = this.querySelector(selectors.progressbar)

    this.thresholdCents = parseInt(this.getAttribute('data-threshold-cents'), 10) || 0
    this.showWhenEmpty = this.getAttribute('data-show-when-empty') === 'true'
    this.lastRenderedCents = parseInt(this.getAttribute('data-current-cents'), 10) || 0
    this.wasQualified = this.getAttribute('data-qualified') === 'true'
    this._celebrateTimer = null

    this._onCartUpdate = this._onCartUpdate.bind(this)
    this._onProductAdded = this._onProductAdded.bind(this)
    this._onPageShow = this._onPageShow.bind(this)

    document.addEventListener('cart:updated', this._onCartUpdate, { signal: this.abortController.signal })
    document.addEventListener('ajaxProduct:added', this._onProductAdded, { signal: this.abortController.signal })
    window.addEventListener('pageshow', this._onPageShow, { signal: this.abortController.signal })

    if (this.getAttribute('data-context') === 'sticky' && typeof ResizeObserver === 'function') {
      this._writeStickyHeight()
      this._resizeObserver = new ResizeObserver(this._writeStickyHeight.bind(this))
      this._resizeObserver.observe(this)
    }
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort()
    if (this._celebrateTimer) {
      clearTimeout(this._celebrateTimer)
      this._celebrateTimer = null
    }
    if (this._resizeObserver) {
      this._resizeObserver.disconnect()
      this._resizeObserver = null
    }
  }

  _onCartUpdate(event) {
    const cart = event && event.detail && event.detail.cart
    if (!cart) return
    this._render(cart.total_price, cart.item_count)
  }

  _onProductAdded(event) {
    // `ajaxProduct:added` fires on both success (detail.product present) and
    // 422 failure (no detail). Only refresh on success — otherwise we waste
    // a /cart.js GET for every failed ATC (out-of-stock, qty limits).
    const succeeded = event && event.detail && event.detail.product
    if (!succeeded) return
    this._refreshFromServer()
  }

  _onPageShow(event) {
    if (!event.persisted) return
    this._refreshFromServer()
  }

  _refreshFromServer() {
    if (!window.theme || !theme.cart || typeof theme.cart.getCart !== 'function') return
    theme.cart.getCart()
      .then(function (cart) {
        if (!cart) return
        this._render(cart.total_price, cart.item_count)
      }.bind(this))
      .catch(function () {
        // Fetch failed (network error, 429, non-JSON). Bar stays at the
        // last-known server-rendered state — silent degradation is the
        // correct UX here. Do not surface a console error or UI toast.
      })
  }

  _writeStickyHeight() {
    const h = this.offsetHeight
    if (h > 0) document.documentElement.style.setProperty('--fsb-sticky-height', h + 'px')
  }

  _render(totalCents, itemCount) {
    if (this.thresholdCents <= 0) return

    totalCents = parseInt(totalCents, 10) || 0
    itemCount = parseInt(itemCount, 10) || 0

    const qualified = totalCents >= this.thresholdCents
    const shouldHide = itemCount === 0 && !this.showWhenEmpty && !qualified
    if (shouldHide) {
      this.setAttribute('hidden', '')
      return
    }
    this.removeAttribute('hidden')

    // Early-return is safe: the displayed content is a pure function of
    // totalCents (message, fill %, qualified state, aria-valuenow) and
    // wasQualified only advances on state change, never regresses here.
    if (totalCents === this.lastRenderedCents) return
    this.lastRenderedCents = totalCents

    const remaining = Math.max(0, this.thresholdCents - totalCents)
    const pct = Math.min(100, (totalCents / this.thresholdCents) * 100)

    this.setAttribute('data-qualified', qualified ? 'true' : 'false')
    this.fillEl.style.width = pct + '%'
    this.progressbarEl.setAttribute('aria-valuenow', String(Math.round(pct)))
    // innerHTML is intentional: theme.strings.shippingBar* can contain
    // <sup>/<strong> from theme.Currency.formatMoney's superscript mode
    // and from Liquid `| t` HTML-suffixed keys. Values are produced by
    // trusted code paths (server-side Liquid, theme money formatter) —
    // never user-supplied. Do not switch to textContent without first
    // removing the superscript-price feature.
    this.messageEl.innerHTML = this._messageFor(qualified, remaining, itemCount)

    if (qualified && !this.wasQualified && !this._reducedMotion()) {
      // Defer the class-add to the next frame. On ATC-from-PDP the cart
      // drawer goes display:none → block in the same tick as this render;
      // per CSS spec, animations started while an ancestor is display:none
      // are cancelled, so the shine would never play on the drawer
      // instance. rAF guarantees the element is laid out before the
      // animation-name property changes.
      window.requestAnimationFrame(function () {
        this.classList.add('is-celebrating')
        this._celebrateTimer = window.setTimeout(function () {
          this.classList.remove('is-celebrating')
          this._celebrateTimer = null
        }.bind(this), 950)
      }.bind(this))
    }
    this.wasQualified = qualified
  }

  _messageFor(qualified, remainingCents, itemCount) {
    const strings = (window.theme && theme.strings) || {}
    const format = (window.theme && theme.Currency && theme.Currency.formatMoney) || null
    const moneyFormat = (window.theme && theme.settings && theme.settings.moneyFormat) || ''

    if (qualified) {
      return strings.shippingBarQualified || ''
    }

    if (itemCount === 0 && this.showWhenEmpty) {
      const threshold = format ? format(this.thresholdCents, moneyFormat) : ''
      return (strings.shippingBarEmpty || '').replace('[threshold]', threshold)
    }

    const remaining = format ? format(remainingCents, moneyFormat) : ''
    return (strings.shippingBarBelow || '').replace('[remaining]', remaining)
  }

  _reducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  }
}

customElements.define('free-shipping-bar', FreeShippingBar)
